/**
 * Figure S7's "Live disclosures (N) · masked exactly as each entity sees them".
 *
 * Every ladder any entity this portfolio can see is on, at its current rung,
 * masked exactly as that entity sees it: a counterparty is a name only from
 * rung 4, after a mutual NDA. The portfolio watches; only the entity consents.
 */
import RungMeter from './RungMeter'
import {
  DISCLOSURE_FOOTNOTE, DISCLOSURE_STATUS_TONE, entitiesOnLaddersLine,
} from './disclosureRows'
import type { DisclosureRow } from './disclosureRows'

export interface AcrossDisclosuresProps {
  rows: DisclosureRow[]
  /**
   * How many of this portfolio's own entities are a party to one of these
   * ladders — the SAME number, from the same function, that S6's "Live
   * disclosures" tile prints under its count. Both pages say it out loud so a
   * reader moving between them can check that they agree.
   */
  entityCount?: number
}

export default function AcrossDisclosures({ rows, entityCount }: AcrossDisclosuresProps) {
  return (
    <div data-part="AcrossDisclosures" className="light-card p-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className="text-[15px] font-bold text-iflow-900">Live disclosures ({rows.length})</h3>
        <span className="text-[12px] text-gray-500">
          · masked exactly as each entity sees them
          {typeof entityCount === 'number' && rows.length > 0
            ? ` · ${entitiesOnLaddersLine(entityCount)}`
            : ''}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-gray-600">
          None of your entities is on a disclosure ladder yet.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead>
              <tr className="bg-iflow-900 text-white">
                <th className="rounded-l-lg px-3 py-2 font-semibold">Entity</th>
                <th className="px-3 py-2 font-semibold">Counterparty</th>
                <th className="px-3 py-2 font-semibold">Rung</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="rounded-r-lg px-3 py-2 font-semibold">Pending action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.agreement.agreement_id}
                  data-testid="across-disclosure-row"
                  className="border-b border-iflow-900/5"
                >
                  <td className="px-3 py-2 align-top">
                    <b className="text-iflow-900">{row.entityLabel}</b>
                  </td>
                  <td className="px-3 py-2 align-top text-gray-700">
                    {row.counterpartyLabel}
                    {row.counterpartyNote && (
                      <div className="text-[11.5px] text-gray-500">{row.counterpartyNote}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <RungMeter level={row.level} ndaPending={row.ndaPending} caption={row.rungCaption} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${DISCLOSURE_STATUS_TONE[row.status]}`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top text-gray-500">{row.pendingAction}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="px-3 pt-3 text-[11.5px] italic text-gray-500">
                  {DISCLOSURE_FOOTNOTE}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
